/**
 * Smooth single-column drawer navigation and existing right-side panels.
 */
(function () {
  'use strict';

  const SELECTORS = {
    drawer: '#jb-drawer',
    toggle: '.mobile-toggle-wrapper > .mobile-toggle',
    close: '[data-jb-close]',
    accordionToggle: '[data-jb-accordion-toggle]',
    openRightPanel: '[data-open-right-panel]',
    closeRightPanel: '[data-close-right-panel]',
    rightPanel: '[data-right-panel]',
    accountTab: '[data-account-tab]',
    accountForm: '[data-account-form]'
  };

  const OPEN_CLASS = 'is-open';
  const CLOSING_CLASS = 'is-closing';
  const ACTIVE_CLASS = 'is-active';
  const BODY_LOCK_CLASS = 'jb-drawer-open';
  const DRAWER_CLOSE_DURATION = 700;
  let drawer;
  let drawerOpenedBy;
  let closeTimer;
  let suppressDrawerFocus = false;

  function resetDrawerImmediately() {
    if (!drawer) return;
    window.clearTimeout(closeTimer);
    drawer.classList.remove(OPEN_CLASS, CLOSING_CLASS);
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');

    document.querySelectorAll(SELECTORS.toggle).forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.closest('.mobile-toggle-wrapper')?.setAttribute('aria-expanded', 'false');
      const details = toggle.closest('details');
      if (details) details.removeAttribute('open');
    });

    resetAccordions();
    if (!hasOpenRightPanel()) document.body.classList.remove(BODY_LOCK_CLASS);
    suppressDrawerFocus = false;
    drawerOpenedBy = null;
  }

  function updateMobileViewport() {
    if (!drawer) return;
    if (!window.matchMedia('(max-width: 767px)').matches) {
      drawer.style.removeProperty('--jb-menu-viewport-height');
      return;
    }
    const viewport = window.visualViewport;
    drawer.style.setProperty('--jb-menu-viewport-height', (viewport?.height || window.innerHeight) + 'px');
  }

  function init() {
    drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    // Safari may restore class/state from its back-forward cache. Always begin
    // a new page view with the navigation fully closed and non-interactive.
    resetDrawerImmediately();
    updateMobileViewport();
    window.addEventListener('resize', updateMobileViewport, { passive: true });
    window.visualViewport?.addEventListener('resize', updateMobileViewport, { passive: true });
    window.addEventListener('pageshow', resetDrawerImmediately);
    window.addEventListener('pagehide', resetDrawerImmediately);

    drawer.querySelectorAll('.jb-drawer__reveal').forEach(function (item, index) {
      item.style.setProperty('--jb-stagger-delay', (150 + (index * 55)) + 'ms');
    });

    document.querySelectorAll(SELECTORS.toggle).forEach(function (toggle) {
      toggle.addEventListener('click', handleToggleClick);
    });

    drawer.querySelectorAll(SELECTORS.close).forEach(function (button) {
      button.addEventListener('click', function () {
        closeDrawer();
      });
    });

    drawer.querySelectorAll(SELECTORS.accordionToggle).forEach(function (button) {
      button.addEventListener('click', handleAccordionToggle);
    });

    document.querySelectorAll(SELECTORS.openRightPanel).forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        openRightPanel(this.getAttribute('data-open-right-panel'), this);
      });
    });

    document.querySelectorAll(SELECTORS.closeRightPanel).forEach(function (button) {
      button.addEventListener('click', closeAllRightPanels);
    });

    document.querySelectorAll(SELECTORS.accountTab).forEach(function (tab) {
      tab.addEventListener('click', function () {
        switchAccountTab(this.getAttribute('data-account-tab'));
      });
    });

    drawer.querySelectorAll(
      '.jb-drawer__link[href], .jb-drawer__sub-link[href], ' +
      '.jb-drawer__third-link[href], .jb-drawer__see-all[href], ' +
      '.jb-drawer__footer-link[href]'
    ).forEach(function (link) {
      link.addEventListener('click', function () {
        closeDrawer();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (hasOpenRightPanel()) {
        closeAllRightPanels();
      } else if (drawer.classList.contains(OPEN_CLASS)) {
        closeDrawer();
      }
    });

    document.addEventListener('click', function (event) {
      if (!hasOpenRightPanel()) return;
      const clickedInsidePanel = event.target.closest('.jb-right-panel');
      const clickedOpener = event.target.closest(SELECTORS.openRightPanel);
      if (!clickedInsidePanel && !clickedOpener) closeAllRightPanels();
    });

    document.addEventListener('jb:search-open', function () {
      if (drawer.classList.contains(OPEN_CLASS)) {
        suppressDrawerFocus = true;
        closeDrawer();
      }
      closeAllRightPanels();
    });

    document.addEventListener('jb:contact-open', function () {
      if (drawer.classList.contains(OPEN_CLASS)) {
        suppressDrawerFocus = true;
        closeDrawer();
      }
      closeAllRightPanels();
    });

    document.addEventListener('jb:request-open', function () {
      if (drawer.classList.contains(OPEN_CLASS)) {
        suppressDrawerFocus = true;
        closeDrawer();
      }
      closeAllRightPanels();
    });
  }

  function handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (drawer.classList.contains(OPEN_CLASS)) {
      closeDrawer();
    } else {
      openDrawer(event.currentTarget);
    }

    const details = event.currentTarget.closest('details');
    if (details) details.removeAttribute('open');
  }

  function openDrawer(opener) {
    window.clearTimeout(closeTimer);
    closeAllRightPanels();
    resetAccordions();
    updateMobileViewport();
    drawerOpenedBy = opener || document.activeElement;
    drawer.classList.remove(CLOSING_CLASS);
    drawer.classList.add(OPEN_CLASS);
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_LOCK_CLASS);

    document.querySelectorAll(SELECTORS.toggle).forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
      toggle.closest('.mobile-toggle-wrapper')?.setAttribute('aria-expanded', 'true');
    });

    document.dispatchEvent(new CustomEvent('jb:menu-open'));

    window.requestAnimationFrame(function () {
      // The overlay also has data-jb-close; focus the actual close button.
      if (drawer.classList.contains(OPEN_CLASS)) drawer.querySelector('button' + SELECTORS.close)?.focus({ preventScroll: true });
    });
  }

  function closeDrawer() {
    if (!drawer || !drawer.classList.contains(OPEN_CLASS)) return;
    window.clearTimeout(closeTimer);
    drawer.classList.remove(OPEN_CLASS);
    drawer.classList.add(CLOSING_CLASS);
    drawer.setAttribute('aria-hidden', 'true');

    document.querySelectorAll(SELECTORS.toggle).forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.closest('.mobile-toggle-wrapper')?.setAttribute('aria-expanded', 'false');
      const details = toggle.closest('details');
      if (details) details.removeAttribute('open');
    });

    document.dispatchEvent(new CustomEvent('jb:menu-close'));

    if (!suppressDrawerFocus && drawerOpenedBy && typeof drawerOpenedBy.focus === 'function') {
      drawerOpenedBy.focus({ preventScroll: true });
    }

    closeTimer = window.setTimeout(function () {
      drawer.classList.remove(CLOSING_CLASS);
      drawer.setAttribute('inert', '');
      resetAccordions();
      if (!hasOpenRightPanel()) document.body.classList.remove(BODY_LOCK_CLASS);
      suppressDrawerFocus = false;
      drawerOpenedBy = null;
    }, DRAWER_CLOSE_DURATION);
  }

  function handleAccordionToggle(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    const item = button.closest('li');
    if (!panel || !item) return;

    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
    const list = item.parentElement;

    if (list) {
      Array.from(list.children).forEach(function (sibling) {
        if (sibling === item) return;
        const siblingButton = sibling.querySelector(':scope > ' + SELECTORS.accordionToggle);
        if (siblingButton) closeAccordion(siblingButton);
      });
    }

    if (shouldOpen) {
      button.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      item.classList.add(ACTIVE_CLASS);
    } else {
      closeAccordion(button);
    }
  }

  function closeAccordion(button) {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    button.setAttribute('aria-expanded', 'false');
    button.closest('li')?.classList.remove(ACTIVE_CLASS);
    if (!panel) return;
    panel.setAttribute('aria-hidden', 'true');
    panel.querySelectorAll(SELECTORS.accordionToggle).forEach(function (nestedButton) {
      nestedButton.setAttribute('aria-expanded', 'false');
      nestedButton.closest('li')?.classList.remove(ACTIVE_CLASS);
      const nestedId = nestedButton.getAttribute('aria-controls');
      if (nestedId) document.getElementById(nestedId)?.setAttribute('aria-hidden', 'true');
    });
  }

  function resetAccordions() {
    if (!drawer) return;
    drawer.querySelectorAll(SELECTORS.accordionToggle).forEach(function (button) {
      button.setAttribute('aria-expanded', 'false');
      button.closest('li')?.classList.remove(ACTIVE_CLASS);
    });
    drawer.querySelectorAll('.jb-drawer__inline-submenu').forEach(function (panel) {
      panel.setAttribute('aria-hidden', 'true');
    });
  }

  function openRightPanel(panelId, opener) {
    closeAllRightPanels();
    const panel = document.querySelector('[data-right-panel="' + panelId + '"]');
    if (!panel) return;

    const returnTarget = drawer?.classList.contains(OPEN_CLASS) ? drawerOpenedBy : (opener || document.activeElement);
    if (drawer?.classList.contains(OPEN_CLASS)) {
      suppressDrawerFocus = true;
      closeDrawer();
    }
    panel._openedBy = returnTarget;
    panel._trigger = opener;
    opener?.setAttribute('aria-expanded', 'true');
    panel.classList.add(OPEN_CLASS);
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_LOCK_CLASS);
    document.dispatchEvent(new CustomEvent('jb:panel-open'));
    panel.querySelector(SELECTORS.closeRightPanel)?.focus({ preventScroll: true });
  }

  function closeAllRightPanels() {
    document.querySelectorAll(SELECTORS.rightPanel).forEach(function (panel) {
      const wasOpen = panel.classList.contains(OPEN_CLASS);
      panel.classList.remove(OPEN_CLASS);
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
      if (wasOpen && panel._openedBy && typeof panel._openedBy.focus === 'function') {
        panel._openedBy.focus({ preventScroll: true });
      }
      panel._openedBy = null;
      panel._trigger?.setAttribute('aria-expanded', 'false');
      panel._trigger = null;
    });
    if (!drawer?.classList.contains(OPEN_CLASS) && !drawer?.classList.contains(CLOSING_CLASS)) {
      document.body.classList.remove(BODY_LOCK_CLASS);
    }
  }

  function hasOpenRightPanel() {
    return document.querySelector('.jb-right-panel.' + OPEN_CLASS) !== null;
  }

  function switchAccountTab(tabId) {
    document.querySelectorAll(SELECTORS.accountTab).forEach(function (tab) {
      tab.classList.toggle(ACTIVE_CLASS, tab.getAttribute('data-account-tab') === tabId);
    });
    document.querySelectorAll(SELECTORS.accountForm).forEach(function (form) {
      form.style.display = form.getAttribute('data-account-form') === tabId ? 'block' : 'none';
    });

    const panel = document.getElementById('jb-panel-account');
    const title = panel?.querySelector('.jb-right-panel__title');
    if (title) title.textContent = tabId === 'register' ? 'CREATE AN ACCOUNT' : 'LOG IN';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();



